/* Forum likes: development samples stay in memory; real likes use the signed app proxy. */
document.addEventListener('alpine:init', () => {
  /** Blog is sent separately; never send cs-maker-space/topic-53 as the handle. */
  const bareHandle = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const parts = raw.split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : '';
  };

  const visibleLikes = new Map();

  Alpine.store('likes', {
    enabled: false, preview: false, signedIn: false, entries: {}, epoch: 0,
    refreshTimer: null, refreshing: false, refreshDelay: 10000,
    bareHandle,
    key(blog, handle) {
      const h = bareHandle(handle);
      return blog && h ? `${blog}/${h}` : '';
    },
    init() {
      const root = document.querySelector('[data-likes-enabled]');
      if (!root) return;
      this.signedIn = root.dataset.cfAuth === '1';
      this.preview = this.signedIn && root.dataset.likesPreview === 'true' && root.dataset.likesEnabled !== 'true';
      this.enabled = this.preview || root.dataset.likesEnabled === 'true';
      window.addEventListener('modl-auth-lost', () => { this.epoch++; this.enabled = false; this.entries = {}; clearTimeout(this.refreshTimer); });
      window.addEventListener('pagehide', () => { this.epoch++; this.entries = {}; clearTimeout(this.refreshTimer); });
      window.addEventListener('pageshow', () => this.scheduleRefresh());
      document.addEventListener('visibilitychange', () => {
        clearTimeout(this.refreshTimer);
        if (!document.hidden) void this.refreshVisible();
      });
      this.scheduleRefresh();
    },
    scheduleRefresh() {
      clearTimeout(this.refreshTimer);
      if (this.enabled && !this.preview && !document.hidden)
        this.refreshTimer = setTimeout(() => this.refreshVisible(), this.refreshDelay);
    },
    async refreshVisible() {
      if (this.refreshing || !this.enabled || this.preview || document.hidden) return;
      this.refreshing = true;
      try {
        const groups = new Map();
        for (const [element, post] of visibleLikes) {
          if (!element.isConnected || !element.getClientRects().length) continue;
          if (!groups.has(post.blog)) groups.set(post.blog, []);
          groups.get(post.blog).push(post);
        }
        for (const [blog, items] of groups) {
          if (!this.enabled || document.hidden) break;
          await this.loadCounts(items, blog, true);
        }
        this.refreshDelay = 10000;
      } catch {
        // Keep the last good counts during a connection failure.
        this.refreshDelay = Math.min(this.refreshDelay * 2, 60000);
      } finally {
        this.refreshing = false;
        this.scheduleRefresh();
      }
    },
    entry(key) {
      if (!this.entries[key]) this.entries[key] = { count: 0, liked: false, ready: false, loading: false, busy: false, error: '', revision: 0 };
      return this.entries[key];
    },
    async loadCounts(items, blog, refresh = false) {
      if (!this.enabled) throw new Error('Likes are not connected yet.');
      if (this.preview) return;
      const epoch = this.epoch;
      const missing = [...new Map(
        items
          .map((item) => {
            const handle = bareHandle(item?.handle || item?.article_handle);
            return handle ? [handle, { ...item, handle }] : null;
          })
          .filter(Boolean)
      ).values()].filter((item) => {
        const state = this.entries[this.key(blog, item.handle)];
        return refresh ? !state?.busy && !state?.loading : !state?.ready;
      });
      const snapshots = new Map(missing.map(item => {
        const state = this.entry(this.key(blog, item.handle));
        return [item.handle, {state, revision: state.revision}];
      }));
      const apply = (item, data) => {
        const {state, revision} = snapshots.get(item.handle);
        if (data && !state.busy && !state.loading && state.revision === revision && (refresh || !state.ready))
          Object.assign(state, data, {ready: true, error: ''});
      };
      const numeric = item => String(item.id || item.article_id || item.admin_graphql_api_id || '').split('/').pop();
      const withIds = missing.filter(item => /^[1-9][0-9]{0,19}$/.test(numeric(item)));
      for (let offset = 0; offset < withIds.length; offset += 100) {
        const batch = withIds.slice(offset, offset + 100);
        const data = this.signedIn
          ? await this.api('/counts', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ article_ids: batch.map(numeric) }) })
          : await this.api('/counts?article_ids=' + encodeURIComponent(batch.map(numeric).join(',')));
        if (epoch !== this.epoch) throw new Error('Please sign in again.');
        for (const item of batch) {
          apply(item, data.counts?.[numeric(item)]);
        }
      }
      // Newly created posts can temporarily have a handle but no numeric ID.
      for (const item of missing.filter(item => !withIds.includes(item))) {
        const data = await this.api('?blog=' + encodeURIComponent(blog) + '&handle=' + encodeURIComponent(item.handle));
        if (epoch !== this.epoch) throw new Error('Please sign in again.');
        apply(item, data);
      }
    },
    async api(path, options) {
      if (!this.signedIn && options?.method && options.method !== 'GET') throw new Error('Sign in to like this post.');
      const response = this.signedIn
        ? await window.ModlCommunityAPI.request('/api/likes' + path, options)
        : await window.ModlCommunityAPI.publicLikes(path);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(response.status === 404 ? 'Likes are not connected yet.' : data.error || 'Could not update likes. Please try again.');
      return data;
    }
  });
  Alpine.data('ForumLike', (blog, handle, articleId) => ({
    blog: String(blog || ''), handle: bareHandle(handle), peopleOpen: false, peopleLoading: false,
    people: [], cursor: null, peopleError: '', alive: true,
    get key() { return this.$store.likes.key(this.blog, this.handle); },
    get state() { return this.$store.likes.entry(this.key); },
    get available() { return this.$store.likes.enabled && !!this.blog && !!this.handle; },
    query() { return '?blog=' + encodeURIComponent(this.blog) + '&handle=' + encodeURIComponent(this.handle); },
    init() {
      visibleLikes.set(this.$el, {blog: this.blog, handle: this.handle, id: articleId});
      if (this.available) void this.load();
    },
    destroy() { this.alive = false; visibleLikes.delete(this.$el); },
    async load() {
      const store = this.$store.likes, state = this.state, epoch = store.epoch;
      if (state.loading || state.busy) return;
      if (store.preview) { state.ready = true; return; }
      state.loading = true;
      try {
        const data = await store.api(this.query());
        if (store.epoch === epoch) Object.assign(state, data, { ready: true, error: '' });
      } catch (e) { if (store.epoch === epoch) state.error = e.message; }
      finally { state.loading = false; }
    },
    async toggle() {
      const store = this.$store.likes, state = this.state, epoch = store.epoch;
      if (!this.available || state.busy || state.loading) return;
      if (!store.signedIn) {
        window.location.assign('/customer_authentication/login?return_to=' + encodeURIComponent(window.location.pathname + window.location.search + window.location.hash));
        return;
      }
      if (!state.ready) { await this.load(); return; }
      const previous = {liked: state.liked, count: state.count};
      const liked = !previous.liked;
      state.busy = true; state.revision++;
      Object.assign(state, {liked, count: Math.max(0, previous.count + (liked ? 1 : -1)), error: ''});
      this.peopleOpen = false;
      try {
        if (liked && window.ModlCommunityTerms && !await window.ModlCommunityTerms.ensure()) {
          if (store.epoch === epoch) Object.assign(state, previous);
          return;
        }
        if (store.epoch !== epoch) return;
        const data = store.preview ? {liked, count: state.count} :
          await store.api('', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ blog: this.blog, handle: this.handle, liked }) });
        if (store.epoch === epoch) Object.assign(state, data, {error: ''});
      } catch (e) {
        if (store.epoch === epoch) Object.assign(state, previous, {error: e.message});
      } finally { state.busy = false; }
    },
    async showPeople(more = false) {
      if (!this.available || this.peopleLoading) return;
      this.peopleOpen = true; this.peopleLoading = true; this.peopleError = '';
      const store = this.$store.likes, epoch = store.epoch;
      try {
        const root = document.querySelector('[data-likes-enabled]');
        const data = store.preview ? { people: this.state.liked ? [{ id: root.dataset.customerId, name: 'You (preview)', image: '' }] : [], next_cursor: null } :
          await store.api('/people' + this.query() + (more && this.cursor ? '&cursor=' + encodeURIComponent(this.cursor) : ''));
        if (store.epoch !== epoch || !this.alive) return;
        this.people = [...new Map([...(more ? this.people : []), ...data.people].map(p => [p.id, p])).values()];
        this.cursor = data.next_cursor;
      } catch (e) { if (store.epoch === epoch && this.alive) this.peopleError = e.message; }
      finally { this.peopleLoading = false; }
    },
    profileUrl(person) {
      const url = new URL(window.location.href);
      url.searchParams.set('member', person.id);
      ['account', 'selected', 'post', 'tag', 'dm', 'conversation', 'view'].forEach(key => url.searchParams.delete(key)); url.hash = '';
      return url.pathname + url.search;
    }
  }));
});
